import { AssistantPlanProvider, useAssistantPlan } from "@/components/assistant/AssistantPlanContext";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import {
  CHAT_PANEL_WIDTH_DEFAULT,
  CHAT_PANEL_WIDTH_MAX,
  clampChatPanelWidth,
} from "@/lib/chat-panel-width";
import { cn } from "@/lib/utils";
import { GripVertical, Sparkles } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";
import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  showTrigger: boolean;
  triggerLabel: string;
  assistant: ReactNode;
  children: ReactNode;
  className?: string;
};

const PLAN_MODE_MIN_CHAT_WIDTH = 640;

function InlineAssistantLayoutInner({
  open,
  onOpenChange,
  showTrigger,
  triggerLabel,
  assistant,
  children,
  className,
}: Props) {
  const { open: planOpen, setOpen: setPlanOpen } = useAssistantPlan();
  const planWorkspaceActive = open && planOpen;
  const location = useLocation();
  const lastPathnameRef = useRef(location.pathname);

  const [chatWidth, setChatWidth] = useLocalStorage<number>(
    "inline-assistant-chat-width",
    CHAT_PANEL_WIDTH_DEFAULT
  );
  const resizeDragRef = useRef<{ startX: number; startWidth: number } | null>(null);

  useEffect(() => {
    const clamped = clampChatPanelWidth(chatWidth);
    if (clamped !== chatWidth) setChatWidth(clamped);
  }, [chatWidth, setChatWidth]);

  useEffect(() => {
    if (lastPathnameRef.current === location.pathname) return;
    lastPathnameRef.current = location.pathname;
    setPlanOpen(false);
  }, [location.pathname, setPlanOpen]);

  useEffect(() => {
    if (!planWorkspaceActive) return;
    if (chatWidth >= PLAN_MODE_MIN_CHAT_WIDTH) return;
    setChatWidth(CHAT_PANEL_WIDTH_MAX);
  }, [planWorkspaceActive, chatWidth, setChatWidth]);

  useEffect(() => {
    if (!planWorkspaceActive) return;
    resizeDragRef.current = null;
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  }, [planWorkspaceActive]);

  useEffect(() => {
    return () => {
      resizeDragRef.current = null;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, []);

  const handleResizePointerDown = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      event.preventDefault();
      resizeDragRef.current = { startX: event.clientX, startWidth: chatWidth };
      event.currentTarget.setPointerCapture(event.pointerId);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    },
    [chatWidth]
  );

  const handleResizePointerMove = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      if (!resizeDragRef.current) return;
      const { startX, startWidth } = resizeDragRef.current;
      const next = clampChatPanelWidth(startWidth + (startX - event.clientX));
      setChatWidth(next);
    },
    [setChatWidth]
  );

  const handleResizePointerUp = useCallback((event: React.PointerEvent<HTMLButtonElement>) => {
    if (resizeDragRef.current) {
      resizeDragRef.current = null;
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch {
        // ignore if already released
      }
    }
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  }, []);

  const panelWidth = clampChatPanelWidth(chatWidth);
  const panelWidthStyle = {
    "--inline-assistant-chat-w": `${panelWidth}px`,
  } as CSSProperties;

  return (
    <div
      className={cn(
        "relative flex min-h-0 flex-col",
        open && "min-h-0 flex-1",
        className
      )}
    >
      <div
        className={cn(
          "flex flex-col gap-4",
          open
            ? "min-h-0 flex-1 lg:flex-row lg:items-stretch"
            : "lg:flex-row lg:items-start"
        )}
      >
        {open && !planWorkspaceActive ? (
          <div className="min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain">
            {children}
          </div>
        ) : null}
        {!open ? <div className="min-w-0 flex-1">{children}</div> : null}
        {open ? (
          <div
            className={cn(
              "flex min-h-0 w-full shrink-0 flex-col max-lg:min-h-[min(420px,45dvh)] lg:min-h-0 lg:flex-row",
              planWorkspaceActive ? "min-w-0 flex-1 lg:w-full" : "lg:w-auto"
            )}
            style={panelWidthStyle}
          >
            {!planWorkspaceActive ? (
              <button
                type="button"
                aria-label="Resize assistant chat"
                className={cn(
                  "group relative hidden w-3 shrink-0 cursor-col-resize touch-none items-center justify-center rounded-md lg:flex",
                  "border-0 bg-transparent p-0 outline-none",
                  "hover:bg-[rgba(15,23,42,0.06)] focus-visible:ring-2 focus-visible:ring-violet-500/50",
                  "dark:hover:bg-white/10"
                )}
                onPointerDown={handleResizePointerDown}
                onPointerMove={handleResizePointerMove}
                onPointerUp={handleResizePointerUp}
                onPointerCancel={handleResizePointerUp}
              >
                <span className="pointer-events-none flex h-16 w-1.5 items-center justify-center rounded-full bg-[rgba(15,23,42,0.08)] transition-colors group-hover:bg-[rgba(15,23,42,0.14)] group-active:bg-[rgba(15,23,42,0.2)] dark:bg-white/10 dark:group-hover:bg-white/25 dark:group-active:bg-white/35">
                  <GripVertical
                    className="h-4 w-4 text-[var(--lm-muted)] opacity-70 group-hover:opacity-100 dark:text-slate-400"
                    aria-hidden
                  />
                </span>
              </button>
            ) : null}
            <aside
              className={cn(
                "flex h-full min-h-0 min-w-0 flex-col",
                planWorkspaceActive
                  ? "w-full flex-1"
                  : "flex-1 lg:w-[var(--inline-assistant-chat-w)] lg:flex-none"
              )}
            >
              <div className="flex h-full min-h-0 flex-col transition-opacity duration-200">
                {assistant}
              </div>
            </aside>
          </div>
        ) : null}
      </div>

      {showTrigger && !open ? (
        <button
          type="button"
          className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-lg shadow-primary/30 transition hover:bg-primary/90 max-lg:bottom-20"
          aria-label={triggerLabel}
          title={triggerLabel}
          onClick={() => onOpenChange(true)}
        >
          <Sparkles className="h-6 w-6 text-white" />
        </button>
      ) : null}
    </div>
  );
}

export function InlineAssistantLayout(props: Props) {
  return (
    <AssistantPlanProvider>
      <InlineAssistantLayoutInner {...props} />
    </AssistantPlanProvider>
  );
}
